import { useQuiz } from '@/hooks/query/quizzes.hooks';
import { useAppSelector } from '@/hooks/store';
import { LoadingPage } from '@/pages/LoadingPage';
import { selectCurrentAccount } from '@/store/features/account';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { Dialog, DialogProps } from 'primereact/dialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SurveyPreview } from '../SurveyPreview';

const StyledDialog = styled(Dialog)`
  width: 65%;
  max-width: 1440px;
  min-height: 700px;

  .p-dialog-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  h1 {
    margin: 0;
    padding: 12px 0;
  }
`;

const StyledHeader = styled(FlexContainer)`
  width: 100%;
  padding: var(--default-padding);
  border-bottom: 1px solid var(--gray-dark);
  background-color: var(--white-main);
`;

const StyledContent = styled(FlexContainer)`
  background-color: var(--beige-main);
  width: 100%;
  height: 100%;
  min-height: 600px;
`;

const StyledFooter = styled(FlexContainer)`
  width: 100%;
  padding: var(--default-padding);
  border-top: 1px solid var(--gray-dark);
  background-color: var(--white-main);
`;

type SurveyPreviewModalProps = {
  surveyId?: string;
} & DialogProps;

export const SurveyPreviewModal: React.FC<SurveyPreviewModalProps> = ({
  surveyId,
  visible,
  onHide,
}) => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);

  const { quiz: survey, isLoading } = useQuiz(currentAccount?.id as string, {
    quizId: surveyId,
    enabled: !!surveyId && !!visible,
    staleTime: Infinity,
  });

  return (
    <StyledDialog
      id="survey-preview-modal"
      headerStyle={{ display: 'none' }}
      blockScroll
      visible={visible}
      onHide={onHide}
      draggable={false}
      dismissableMask
      closeOnEscape
      data-testid="survey-preview-modal"
    >
      {/* Header */}
      <StyledHeader justify="space-between" align="center">
        <h1>{survey?.title || t('security.culture.survey.full')}</h1>
        <AppButton
          icon="pi pi-times"
          onClick={onHide}
          type="text"
          severity="secondary"
          rounded
          size="sm"
        />
      </StyledHeader>

      {/* Content */}
      <StyledContent
        direction="column"
        align="center"
        justify="center"
        translate="no"
        className="notranslate"
      >
        {isLoading && (
          <LoadingPage message={t('security.culture.survey.loading')} />
        )}

        {!isLoading && survey && <SurveyPreview survey={survey} />}
      </StyledContent>

      {/* Footer */}
      <StyledFooter justify="flex-end">
        <AppButton
          id="survey-preview-modal-close"
          label={t('button.close')}
          severity="secondary"
          type="outlined"
          onClick={onHide}
          data-testid="survey-preview-modal-close"
        />
      </StyledFooter>
    </StyledDialog>
  );
};
